import { useState, useEffect, useCallback } from "react";
import { env } from "@/config/env";
import { logger } from "@/lib/logger";
import type { InfraAgent } from "@/types";
import { apiService, ApiError } from "@/lib/api";
import { transformApiAgent } from "@/lib/data-transform";

export interface UseAgentsOptions {
  initialLimit?: number;
  search?: string;
  tags?: string[];
  competency?: string;
  sortBy?: string;
  autoLoad?: boolean;
}

export interface UseAgentsReturn {
  agents: InfraAgent[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  total: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  addAgent: (agent: InfraAgent) => void;
  removeAgent: (id: string) => void;
  updateAgent: (id: string, updates: Partial<InfraAgent>) => void;
  loadingMore: boolean;
}

export function useAgents(options: UseAgentsOptions = {}): UseAgentsReturn {
  const {
    initialLimit = 6,
    search = "",
    tags = [],
    competency = "",
    sortBy = "newest",
    autoLoad = true,
  } = options;

  const [agents, setAgents] = useState<InfraAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const loadAgents = useCallback(
    async (reset = false) => {
      const currentOffset = reset ? 0 : offset;
      const isLoadingMore = currentOffset > 0;

      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        const response = await apiService.getAgents(
          initialLimit,
          currentOffset,
          search || undefined,
          tags.length > 0 ? tags : undefined,
          competency || undefined,
          sortBy || undefined,
        );

        const transformedAgents = response.agents.map(transformApiAgent);

        if (reset) {
          setAgents(transformedAgents);
          setOffset(initialLimit);
        } else {
          setAgents((prev) => [...prev, ...transformedAgents]);
          setOffset((prev) => prev + initialLimit);
        }

        setHasMore(response.pagination.hasMore);
        setTotal(response.pagination.total);
      } catch (err) {
        let errorMessage = "Failed to load agents";

        if (err instanceof ApiError) {
          // Handle specific API errors with user-friendly messages
          if (err.status === 0) {
            errorMessage =
              "Unable to connect to the service. Please check your internet connection.";
          } else if (err.status >= 500) {
            errorMessage =
              "Service temporarily unavailable. Please try again later.";
          } else if (err.message.includes("Service temporarily unavailable")) {
            errorMessage =
              "Backend service is currently unavailable. Please try again later.";
          } else {
            errorMessage = err.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        logger.error("Error loading agents:", err);

        // If it's the initial load and we're in production, don't show technical error details
        if (reset && env.IS_PRODUCTION) {
          setError("Service temporarily unavailable. Please try again later.");
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [initialLimit, search, tags, competency, sortBy, offset],
  );

  const refresh = useCallback(async () => {
    setOffset(0);
    await loadAgents(true);
  }, [loadAgents]);

  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      await loadAgents(false);
    }
  }, [loadAgents, loadingMore, hasMore]);

  const addAgent = useCallback((agent: InfraAgent) => {
    setAgents((prev) => [agent, ...prev]);
    setTotal((prev) => prev + 1);
  }, []);

  const removeAgent = useCallback((id: string) => {
    setAgents((prev) => prev.filter((agent) => agent.id !== id));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  const updateAgent = useCallback(
    (id: string, updates: Partial<InfraAgent>) => {
      setAgents((prev) =>
        prev.map((agent) =>
          agent.id === id ? { ...agent, ...updates } : agent,
        ),
      );
    },
    [],
  );

  // Load agents on mount and when dependencies change
  useEffect(() => {
    if (autoLoad) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tags, competency, sortBy, autoLoad]); // Removed refresh from deps to avoid infinite loop

  return {
    agents,
    loading,
    error,
    hasMore,
    total,
    loadMore,
    refresh,
    addAgent,
    removeAgent,
    updateAgent,
    loadingMore,
  };
}

// Hook for loading a single agent
export function useAgent(id: string) {
  const [agent, setAgent] = useState<InfraAgent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAgent = useCallback(async () => {
    if (!id) return;

    logger.log("🔍 Loading agent with ID:", id);
    setLoading(true);
    setError(null);

    try {
      logger.api.request("GET", `${env.API_BASE_URL}/agents/${id}`);
      const apiAgent = await apiService.getAgent(id);
      logger.api.response("GET", `/agents/${id}`, apiAgent);

      // Track view
      await apiService.viewAgent(id).catch(logger.warn); // Don't fail if view tracking fails

      const transformedAgent = transformApiAgent(apiAgent);
      logger.log("🔄 Transformed agent:", transformedAgent);
      setAgent(transformedAgent);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load agent";
      logger.api.error("GET", `/agents/${id}`, err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const likeAgent = useCallback(async () => {
    if (!agent) return;

    try {
      // Client-side (localStorage) handles rate limiting
      // Backend just increments the counter
      await apiService.likeAgent(agent.id);
      setAgent((prev) =>
        prev
          ? {
              ...prev,
              stats: {
                ...prev.stats,
                likes: prev.stats.likes + 1,
              },
            }
          : null,
      );
      return { success: true };
    } catch (err) {
      logger.error("Error liking agent:", err);
      return { success: false, error: "Failed to like agent" };
    }
  }, [agent]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  return {
    agent,
    loading,
    error,
    refresh: loadAgent,
    likeAgent,
  };
}

// Hook for competencies
export function useCompetencies() {
  const [competencies, setCompetencies] = useState<
    Array<{
      id: string;
      name: string;
      description: string;
      agentCount?: number;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCompetencies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getCompetencies();
      setCompetencies(response.competencies);
    } catch (err) {
      let errorMessage = "Failed to load competencies";

      if (err instanceof ApiError) {
        if (err.status === 0) {
          errorMessage = "Unable to connect to the service";
        } else if (err.status >= 500) {
          errorMessage = "Service temporarily unavailable";
        } else if (err.message.includes("Service temporarily unavailable")) {
          errorMessage = "Backend service is currently unavailable";
        } else {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      logger.error("Error loading competencies:", err);

      // In production, use generic error message
      if (env.IS_PRODUCTION) {
        setError("Service temporarily unavailable");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetencies();
  }, [loadCompetencies]);

  return {
    competencies,
    loading,
    error,
    refresh: loadCompetencies,
  };
}

// Hook for tags
export function useTags() {
  const [tags, setTags] = useState<
    Array<{
      id: string;
      name: string;
      description?: string;
      usageCount?: number;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTags = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getTags();
      setTags(response.tags);
    } catch (err) {
      let errorMessage = "Failed to load tags";

      if (err instanceof ApiError) {
        if (err.status === 0) {
          errorMessage = "Unable to connect to the service";
        } else if (err.status >= 500) {
          errorMessage = "Service temporarily unavailable";
        } else if (err.message.includes("Service temporarily unavailable")) {
          errorMessage = "Backend service is currently unavailable";
        } else {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      logger.error("Error loading tags:", err);

      // In production, use generic error message
      if (env.IS_PRODUCTION) {
        setError("Service temporarily unavailable");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  return {
    tags,
    loading,
    error,
    refresh: loadTags,
  };
}
